import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * <thyra-cart-drawer> - the storefront's only cart drawer.
 *
 * Opens when:
 *   - Horizon's native product form / quick add fires a CartAddEvent (`cart:update`)
 *   - a custom section dispatches `cart:refresh` on `document` after a successful /cart/add.js
 *   - any element carrying `data-thyra-cart-trigger` is clicked (e.g. the header cart icon)
 *
 * Keeps in sync:
 *   - every `[data-thyra-cart-count]` element (header bubble) receives the item count
 *   - `cart:updated` is dispatched on window after drawer-initiated changes, for third parties
 *
 * Content is re-rendered through the Section Rendering API, so Liquid stays the single source of markup.
 */

const ACTIVE_CLASS = 'is-active';
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

class ThyraCartDrawer extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {HTMLElement | null} */
  #returnFocus = null;
  #requestId = 0;
  #isUpdating = false;

  get #sectionId() {
    return /** @type {string} */ (this.dataset.sectionId);
  }

  get #root() {
    return window.Shopify?.routes?.root ?? '/';
  }

  get #drawer() {
    return /** @type {HTMLElement} */ (this.querySelector('.tcd-drawer'));
  }

  get #overlay() {
    return /** @type {HTMLElement} */ (this.querySelector('.tcd-overlay'));
  }

  get #content() {
    return this.querySelector('#ThyraCartDrawer-Content');
  }

  get isOpen() {
    return this.#drawer.classList.contains(ACTIVE_CLASS);
  }

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // Everything is delegated, so swapping the rendered content never requires re-binding.
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    document.addEventListener('click', this.#onTriggerClick, { signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onThemeCartUpdate, { signal });
    document.addEventListener('cart:refresh', this.#onCartRefresh, { signal });
    window.addEventListener('cart:updated', this.#onCartUpdated, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    if (this.isOpen) this.close({ restoreFocus: false });
  }

  /**
   * @param {string} [markup] - Rendered section HTML when the caller already has it (e.g. from /cart/add.js)
   */
  async open(markup) {
    // On the cart page the page itself is the cart, so only keep the header count in sync.
    if (this.hasAttribute('data-open-disabled')) return this.refresh(markup);

    // With fresh markup in hand, render first so nothing re-lays-out while the drawer slides in.
    if (markup !== undefined) {
      await this.refresh(markup);
      this.#show();
      return;
    }

    // Otherwise slide in immediately and swap the content in once the animation has settled.
    await this.refresh(undefined, this.#show());
  }

  /**
   * @param {Partial<{ restoreFocus: boolean }>} [options]
   */
  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;

    this.#drawer.classList.remove(ACTIVE_CLASS);
    this.#overlay.classList.remove(ACTIVE_CLASS);
    this.#lockScroll(false);

    if (restoreFocus && this.#returnFocus?.isConnected) this.#returnFocus.focus({ preventScroll: true });
    this.#returnFocus = null;
  }

  /**
   * Re-renders the drawer from the server. Pass `html` when a response already carries the rendered section.
   * @param {string} [html]
   * @param {Promise<void>} [settled] - Held back until this resolves, so the swap never happens mid-animation
   */
  async refresh(html, settled) {
    const requestId = ++this.#requestId;

    if (html !== undefined) {
      this.#render(html);
      return;
    }

    this.#content?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${this.#root}?section_id=${this.#sectionId}`, {
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Section request failed (${response.status})`);

      const markup = await response.text();
      await settled;
      // A newer request superseded this one while it was in flight.
      if (requestId !== this.#requestId) return;

      this.#render(markup);
    } catch (error) {
      console.error('[thyra-cart-drawer]', error);
      if (requestId === this.#requestId) this.#content?.removeAttribute('aria-busy');
    }
  }

  /**
   * Slides the drawer in. Resolves once the slide has finished (or immediately if it was already open).
   * @returns {Promise<void>}
   */
  #show() {
    if (this.isOpen) return Promise.resolve();

    this.#returnFocus = /** @type {HTMLElement | null} */ (document.activeElement);
    this.#lockScroll(true);
    this.#drawer.classList.add(ACTIVE_CLASS);
    this.#overlay.classList.add(ACTIVE_CLASS);

    requestAnimationFrame(() => this.querySelector('.js-tcd-close')?.focus({ preventScroll: true }));

    return new Promise((resolve) => {
      const done = () => {
        this.#drawer.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
        resolve();
      };
      /** @param {TransitionEvent} event */
      const onEnd = (event) => {
        if (event.target === this.#drawer && event.propertyName === 'transform') done();
      };
      // Safety net for reduced motion / interrupted transitions.
      const timer = setTimeout(done, 450);
      this.#drawer.addEventListener('transitionend', onEnd);
    });
  }

  /**
   * Freezes page scroll without the scrollbar vanishing, which would reflow the page as the drawer opens.
   * @param {boolean} locked
   */
  #lockScroll(locked) {
    const root = document.documentElement;
    root.style.overflow = locked ? 'hidden' : '';
    root.style.scrollbarGutter = locked ? 'stable' : '';
  }

  /**
   * @param {string} markup - HTML of the rendered section
   */
  #render(markup) {
    const next = new DOMParser().parseFromString(markup, 'text/html').querySelector('#ThyraCartDrawer-Content');
    const current = this.#content;
    if (!next || !current) return;

    const scrollTop = current.querySelector('.tcd-items-wrapper')?.scrollTop ?? 0;
    const activeButton = /** @type {HTMLElement | null} */ (document.activeElement)?.closest?.('.js-tcd-qty-update');
    const { line, dir } = /** @type {HTMLElement | undefined} */ (activeButton)?.dataset ?? {};

    current.replaceWith(next);

    const items = next.querySelector('.tcd-items-wrapper');
    if (items) items.scrollTop = scrollTop;

    // The focused +/- button was just replaced; hand focus to its twin so keyboard users keep their place.
    if (line && this.isOpen) {
      const twin = /** @type {HTMLElement | null} */ (
        next.querySelector(`.js-tcd-qty-update[data-line="${line}"][data-dir="${dir}"]`) ??
          next.querySelector('.js-tcd-close')
      );
      twin?.focus({ preventScroll: true });
    }

    this.#syncCount(Number(next.dataset.itemCount) || 0);
  }

  /**
   * @param {number} count
   */
  #syncCount(count) {
    document.querySelectorAll('[data-thyra-cart-count]').forEach((element) => {
      element.textContent = String(count);
      /** @type {HTMLElement} */ (element).hidden = count < 1;
    });
  }

  /**
   * @param {string} line
   * @param {string} quantity
   * @param {Element | null} row
   */
  async #changeQuantity(line, quantity, row) {
    if (this.#isUpdating) return;
    this.#isUpdating = true;
    row?.classList.add('is-loading');

    try {
      const response = await fetch(`${this.#root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity), sections: this.#sectionId }),
      });
      if (!response.ok) throw new Error(`Cart change failed (${response.status})`);

      const cart = await response.json();
      const markup = cart.sections?.[this.#sectionId];

      // `markup` is undefined only if the section was missing from the response, which falls back to a fetch.
      await this.refresh(markup);

      window.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'thyra-cart-drawer', cart } }));
    } catch (error) {
      console.error('[thyra-cart-drawer]', error);
      // Resync with the server so the UI never shows a quantity the cart doesn't have.
      await this.refresh();
    } finally {
      row?.classList.remove('is-loading');
      this.#isUpdating = false;
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    if (target.closest('.js-tcd-close')) {
      this.close();
      return;
    }

    if (target === this.#overlay) {
      this.close();
      return;
    }

    const qtyButton = /** @type {HTMLElement | null} */ (target.closest('.js-tcd-qty-update'));
    if (qtyButton) {
      event.preventDefault();
      this.#changeQuantity(qtyButton.dataset.line ?? '', qtyButton.dataset.qty ?? '', qtyButton.closest('.tcd-item'));
    }
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    /** @type {Element} */ (event.target).querySelector('.tcd-checkout-btn')?.classList.add('is-loading');
  };

  /**
   * Opens the drawer from any `[data-thyra-cart-trigger]` (header cart icon); the link stays as a no-JS fallback.
   * @param {MouseEvent} event
   */
  #onTriggerClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (this.hasAttribute('data-open-disabled')) return;
    if (!(event.target instanceof Element) || !event.target.closest('[data-thyra-cart-trigger]')) return;

    event.preventDefault();
    this.open();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = /** @type {HTMLElement[]} */ ([...this.#drawer.querySelectorAll(FOCUSABLE)]);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !this.#drawer.contains(active))) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && (active === last || !this.#drawer.contains(active))) {
      event.preventDefault();
      first?.focus();
    }
  };

  /**
   * Horizon's native product form and quick add dispatch `cart:update`. Adds open the drawer; anything else
   * (quantity changes made elsewhere) only refreshes it silently.
   * @param {Event} event
   */
  #onThemeCartUpdate = (event) => {
    if (event instanceof CartAddEvent) {
      this.open();
    } else {
      this.refresh();
    }
  };

  /**
   * Contract for custom sections: after a successful /cart/add.js (requested with `sections=thyra-cart-drawer`),
   * dispatch `cart:refresh` on `document` with `detail: { sections: data.sections }` for a zero-extra-request open.
   */
  #onCartRefresh = (/** @type {Event} */ event) => {
    this.open(/** @type {CustomEvent} */ (event).detail?.sections?.[this.#sectionId]);
  };

  /** @param {Event} event */
  #onCartUpdated = (event) => {
    if (/** @type {CustomEvent} */ (event).detail?.source === 'thyra-cart-drawer') return;
    this.refresh();
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    // Restored from bfcache: the cart may have changed in another tab and the checkout spinner may be stuck.
    this.close({ restoreFocus: false });
    this.refresh();
  };
}

if (!customElements.get('thyra-cart-drawer')) {
  customElements.define('thyra-cart-drawer', ThyraCartDrawer);
}
